/**
 * Partner campaign dashboard (epic-6).
 *
 * Fetches the combined funnel + leads roll-up for one campaign and renders:
 *   - headline KPI cards (conversion rate, consented leads, total reach);
 *   - the scan -> delivery funnel visualisation;
 *   - the traffic-source split;
 *   - the competition / winner / prize-delivery status + handover video link;
 *   - a downloadable CSV report.
 *
 * State handling (never crashes):
 *   - loading       -> skeleton placeholders (reserves layout, no content jump);
 *   - error / 501   -> a clear message (e.g. "not yet available in this env");
 *   - degraded/stale-> a non-blocking banner; best-available numbers still show;
 *   - empty         -> "awaiting data" copy inside each panel.
 *
 * Auth: the data + report endpoints sit behind the fail-closed internal-staff
 * stub (501 in prod) until v1-2-6 (partner-scoped auth) lands — that deferral is
 * what turns this into a self-serve partner dashboard.
 */
import { useCallback, useEffect, useState } from 'react';
import { Icon } from '../../components/Icon';
import {
  ApiError,
  campaignReportUrl,
  fetchCampaignFunnel,
  type CampaignFunnel,
} from '../../lib/api';
import { DataQualityBanner } from './DataQualityBanner';
import { FunnelChart } from './FunnelChart';
import { KpiCard } from './KpiCard';
import { TrafficSplit } from './TrafficSplit';
import { WinnerStatus } from './WinnerStatus';
import { formatAsOf, formatCount, formatRate } from './funnelLabels';

interface Props {
  campaignId: string;
}

type LoadState =
  | { phase: 'loading' }
  | { phase: 'error'; message: string }
  | { phase: 'ready'; data: CampaignFunnel };

function Panel({
  title,
  children,
  className = '',
}: {
  title: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section className={`rounded-2xl border border-line bg-white p-6 shadow-card ${className}`}>
      <h2 className="mb-4 text-base font-semibold text-ink">{title}</h2>
      {children}
    </section>
  );
}

function SkeletonDashboard() {
  return (
    <div className="animate-pulse space-y-6 motion-reduce:animate-none" aria-hidden="true">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-28 rounded-2xl border border-line bg-surface-muted" />
        ))}
      </div>
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="h-80 rounded-2xl border border-line bg-surface-muted lg:col-span-2" />
        <div className="h-80 rounded-2xl border border-line bg-surface-muted" />
      </div>
    </div>
  );
}

export function CampaignDashboard({ campaignId }: Props) {
  const [state, setState] = useState<LoadState>({ phase: 'loading' });

  const load = useCallback(async () => {
    setState({ phase: 'loading' });
    try {
      const data = await fetchCampaignFunnel(campaignId);
      setState({ phase: 'ready', data });
    } catch (e) {
      const message =
        e instanceof ApiError
          ? e.message
          : 'Something went wrong loading this dashboard. Please try again.';
      setState({ phase: 'error', message });
    }
  }, [campaignId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (state.phase === 'loading') {
    return (
      <div>
        <p className="sr-only" role="status">
          Loading campaign metrics…
        </p>
        <SkeletonDashboard />
      </div>
    );
  }

  if (state.phase === 'error') {
    return (
      <div
        role="alert"
        className="rounded-2xl border border-line bg-white p-8 text-center shadow-card"
      >
        <Icon name="alert" className="mx-auto h-8 w-8 text-warm-600" />
        <p className="mt-3 text-sm text-ink-700">{state.message}</p>
        <button
          type="button"
          onClick={() => void load()}
          className="mt-4 inline-flex cursor-pointer items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white transition-colors duration-200 hover:bg-primary-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
        >
          Try again
        </button>
      </div>
    );
  }

  const data = state.data;
  const asOf = formatAsOf(data.as_of);
  const totalReach = data.funnel.qr_scan ?? 0;

  return (
    <div className="space-y-6">
      {/* Header: title + as-of + report download */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-ink">Campaign performance</h1>
          <p className="mt-1 text-sm text-ink-500">
            Campaign{' '}
            <span className="font-medium text-ink-700">{data.campaign_id}</span>
            {asOf ? (
              <>
                {' · '}as of <time dateTime={data.as_of ?? undefined}>{asOf}</time>
              </>
            ) : null}
          </p>
        </div>
        <a
          href={campaignReportUrl(campaignId)}
          className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-primary-200 bg-white px-4 py-2 text-sm font-medium text-primary-700 transition-colors duration-200 hover:bg-primary-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
          download
        >
          <Icon name="download" className="h-4 w-4" />
          Download report (CSV)
        </a>
      </div>

      <DataQualityBanner degraded={data.degraded} stale={data.stale} asOf={data.as_of} />

      {/* KPI row */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <KpiCard
          label="Conversion rate"
          value={formatRate(data.conversion_rate)}
          caption="Scan to completion"
          icon="chart"
        />
        <KpiCard
          label="Consented leads"
          value={formatCount(data.leads)}
          caption="Opted-in, shareable"
          icon="leads"
        />
        <KpiCard
          label="Total reach"
          value={formatCount(totalReach)}
          caption="QR scans at top of funnel"
          icon="qr"
        />
      </div>

      {/* Funnel + status */}
      <div className="grid gap-6 lg:grid-cols-3">
        <Panel title="Conversion funnel" className="lg:col-span-2">
          <FunnelChart funnel={data.funnel} />
        </Panel>
        <Panel title="Competition & prize delivery">
          <WinnerStatus status={data.status} winner={data.winner} />
        </Panel>
      </div>

      {/* Traffic split */}
      <Panel title="Traffic sources">
        <TrafficSplit sources={data.traffic_sources} />
      </Panel>
    </div>
  );
}

export default CampaignDashboard;
